import { GeoFlag, UkFlag } from '../Flags';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
    LayoutDashboard, 
    FileText, 
    Briefcase, 
    Users, 
    Ticket, 
    MessageSquare, 
    Search, 
    Tag, 
    Mail, 
    Activity, 
    BarChart3, 
    LogOut, 
    ExternalLink, 
    Globe,
    Menu, 
    X,
    PanelLeftClose,
    PanelLeftOpen,
    User,
    Building2,
    ChevronDown,
    FileSpreadsheet,
    FolderArchive,
    Calculator,
    ShoppingBag,
    UserCheck,
    Laptop
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import '../../css/shadcn-admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminLayout = ({ children, title = 'ადმინ პანელი' }) => {
    const { user, logout, isSuperAdmin, isBlogManager, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState(0);

    // Ensure Georgian (ka) language is active by default if not set
    useEffect(() => {
        if (!localStorage.getItem('i18n_language')) {
            i18n.changeLanguage('ka');
            localStorage.setItem('i18n_language', 'ka');
        }
    }, [i18n]);

    // Update browser tab document title
    useEffect(() => {
        if (title) {
            document.title = `${title} | OrbitroniX Admin`;
        }
    }, [title]);

    // Guard route access
    useEffect(() => {
        if (!loading) {
            if (!user) {
                navigate('/login');
            } else if (!isSuperAdmin() && !isBlogManager()) {
                navigate('/login');
            }
        }
    }, [user, loading, isSuperAdmin, isBlogManager, navigate]);

    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const profileDropdownRef = useRef(null);

    // Close profile dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
                setProfileDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close mobile drawer and profile dropdown on route change
    useEffect(() => {
        setMobileOpen(false);
        setProfileDropdownOpen(false);
    }, [location.pathname]);

    // Fetch unread messages count
    useEffect(() => {
        const fetchCounts = async () => {
            try {
                const res = await fetch(`${API_URL}contacts.php?action=stats`);
                const data = await res.json();
                if (data && data.new) setUnreadMessages(data.new);
            } catch {
                // Silently handle
            }
        };
        if (user && isSuperAdmin()) {
            fetchCounts();
        }
    }, [user, isSuperAdmin]);

    const navItems = [
        { path: '/admin',                label: t('admin.dashboard.title') || 'მთავარი პანელი',         icon: LayoutDashboard, role: 'all' },
        { path: '/admin/blogs',          label: t('admin.dashboard.blogManagement') || 'ბლოგები',        icon: FileText,        role: 'all' },
        { path: '/admin/blog-categories', label: t('admin.dashboard.blogCategories') || 'ბლოგის კატეგორიები', icon: Tag,      role: 'all', isSubItem: true },
        { path: '/admin/portfolio',      label: t('admin.dashboard.portfolioManagement') || 'პორტფოლიო', icon: Briefcase,       role: 'super_admin' },
        { path: '/admin/portfolio-categories', label: t('admin.dashboard.portfolioCategories') || 'პორტფოლიოს კატეგორიები', icon: Tag, role: 'super_admin', isSubItem: true },
        { path: '/admin/offers',         label: t('admin.dashboard.offers') || 'შეთავაზებების გენერატორი', icon: FileSpreadsheet, role: 'super_admin' },
        { path: '/admin/products',       label: t('admin.dashboard.products') || 'პროდუქტების კატალოგი',   icon: ShoppingBag,     role: 'super_admin' },
        { path: '/admin/resources',      label: t('admin.dashboard.resources') || 'ბრენდ რესურსები',     icon: FolderArchive,   role: 'super_admin' },
        { path: '/admin/municipalities', label: t('admin.dashboard.municipalities') || 'მუნიციპალიტეტები', icon: Building2,     role: 'super_admin', badge: null },
        { path: '/admin/municipalities/report', label: t('admin.dashboard.municipalitiesReport') || 'ანგარიში & კალკულატორი', icon: Calculator, role: 'super_admin', isSubItem: true },
        { path: '/admin/potential-customers', label: t('admin.dashboard.potentialCustomers') || 'პოტენციური კლიენტები', icon: UserCheck, role: 'super_admin' },
        { path: '/admin/users',          label: t('admin.dashboard.userManagement') || 'მომხმარებლები',   icon: Users,           role: 'super_admin' },
        { path: '/admin/tickets',        label: t('admin.tickets.title') || 'ტიკეტები',                  icon: Ticket,          role: 'super_admin' },
        { path: '/admin/categories',     label: t('admin.dashboard.ticketCategories') || 'კატეგორიები',  icon: Tag,             role: 'super_admin' , isSubItem: true },
        { path: '/admin/demo-requests',  label: t('admin.dashboard.demoRequests') || 'დემო მოთხოვნები', icon: Laptop,          role: 'super_admin' },
        { path: '/admin/messages',       label: t('admin.dashboard.contactMessages') || 'შეტყობინებები', icon: MessageSquare,   badge: unreadMessages, role: 'super_admin' },
        { path: '/admin/audits',         label: t('admin.dashboard.webAudits') || 'ვებ აუდიტები',        icon: Search,          role: 'super_admin' },
        { path: '/admin/newsletters',    label: t('admin.dashboard.newsletterSubs') || 'სიახლეები',       icon: Mail,            role: 'super_admin' },
        { path: '/admin/logs',           label: t('admin.dashboard.activityLogs') || 'ლოგები',           icon: Activity,        role: 'super_admin' },
        { path: '/admin/analytics',      label: t('admin.dashboard.deepAnalytics') || 'ანალიტიკა',       icon: BarChart3,       role: 'super_admin' },
    ];

    const getRoleLabel = (role) => {
        switch (role) {
            case 'super_admin': return t('roles.super_admin') || 'სუპერ ადმინი';
            case 'partner': return t('roles.partner') || 'ავტორიზებული პარტნიორი';
            case 'blog_manager': return t('roles.blog_manager') || 'კონტენტის მენეჯერი';
            case 'client': return t('roles.client') || 'კლიენტი';
            default: return role;
        }
    };

    if (loading || !user) return null;

    return (
        <div className={`shadcn-admin-wrapper ${collapsed ? 'shadcn-wrapper-collapsed' : ''}`}>
            {/* Mobile Backdrop */}
            <div 
                className={`mobile-sidebar-overlay ${mobileOpen ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
            />

            {/* ── SIDEBAR ── */}
            <aside className={`shadcn-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
                <div className="sidebar-header">
                    <Link to="/admin" className="brand-logo">
                        <img 
                            src="/orbx.png" 
                            alt="Orbitronix Logo" 
                            className="brand-logo-img"
                        />
                        {!collapsed && <span className="brand-logo-text">OrbitroniX</span>}
                    </Link>
                    


                    <button 
                        className="sidebar-toggle-btn d-lg-none"
                        onClick={() => setMobileOpen(false)}
                    >
                        <X size={18} />
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {!collapsed && <div className="nav-section-title" style={{ color: '#94a3b8' }}>ადმინისტრირება</div>}
                    {navItems.map((item) => {
                        if (item.role === 'super_admin' && !isSuperAdmin()) return null;
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path || 
                            (item.path !== '/admin' && location.pathname.startsWith(item.path));
                        
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`nav-item-link ${isActive ? 'active' : ''} ${item.isSubItem ? 'nav-item-link-sub' : ''}`}
                                title={collapsed ? item.label : undefined}
                                style={item.isSubItem && !collapsed ? { paddingLeft: '2.75rem', fontSize: '0.8125rem', opacity: 0.85 } : undefined}
                            >
                                <Icon size={item.isSubItem ? 15 : 18} />
                                {!collapsed && <span>{item.label}</span>}
                                {!collapsed && item.badge > 0 && (
                                    <span className="nav-item-badge">{item.badge}</span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Sidebar Footer */}
                <div className="sidebar-footer">
                    {!collapsed ? (
                        <div className="d-flex align-items-center justify-content-between w-100">
                            <span className="text-muted smallest" style={{ opacity: 0.5 }}>
                                OrbitroniX Admin v2.0
                            </span>
                            <button 
                                className="sidebar-toggle-btn d-none d-lg-flex"
                                onClick={() => setCollapsed(true)}
                                title="მინიმიზაცია"
                            >
                                <PanelLeftClose size={16} />
                            </button>
                        </div>
                    ) : (
                        <button 
                            className="sidebar-toggle-btn sidebar-toggle-btn-bottom d-none d-lg-flex"
                            onClick={() => setCollapsed(false)}
                            title="გაშლა (მენიუს გახსნა)"
                        >
                            <PanelLeftOpen size={18} />
                        </button>
                    )}
                </div>
            </aside>

            {/* ── MAIN CONTENT AREA ── */}
            <div className="shadcn-main-content">
                {/* ── HEADER BAR ── */}
                <header className="shadcn-header">
                    <div className="d-flex align-items-center gap-3">
                        <button 
                            className="icon-button d-lg-none"
                            onClick={() => setMobileOpen(!mobileOpen)}
                            title="მენიუს გახსნა"
                        >
                            <Menu size={20} />
                        </button>

                        <h2 
                            className="text-dark mb-0 fs-5 fw-bold"
                            style={{ 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                maxWidth: 'calc(100vw - 420px)'
                            }}
                        >{title}</h2>
                    </div>

                    <div className="header-actions">
                        {/* Language Switcher */}
                        <div className="d-flex align-items-center gap-1 bg-light rounded-pill p-1 border" title="ენა / Language">
                            <button
                                type="button"
                                className={`btn btn-sm p-0 rounded-circle border-0 d-flex align-items-center justify-content-center ${i18n.language === 'ka' ? 'bg-white shadow-sm' : 'opacity-50'}`}
                                onClick={() => {
                                    i18n.changeLanguage('ka');
                                    try { localStorage.setItem('i18nextLng', 'ka'); } catch (e) {}
                                }}
                                title="ქართული"
                                style={{ width: '28px', height: '28px', cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                <GeoFlag size={18} />
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm p-0 rounded-circle border-0 d-flex align-items-center justify-content-center ${i18n.language === 'en' ? 'bg-white shadow-sm' : 'opacity-50'}`}
                                onClick={() => {
                                    i18n.changeLanguage('en');
                                    try { localStorage.setItem('i18nextLng', 'en'); } catch (e) {}
                                }}
                                title="English"
                                style={{ width: '28px', height: '28px', cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                <UkFlag size={18} />
                            </button>
                        </div>

                        {/* Direct Link to Main Frontend Site */}
                        <Link 
                            to="/ka" 
                            target="_blank" 
                            className="icon-button"
                            title="ვებგვერდის ნახვა"
                        >
                            <ExternalLink size={18} />
                        </Link>

                        {/* User Profile Dropdown Menu */}
                        <div className="position-relative ms-1" ref={profileDropdownRef}>
                            <div 
                                className="user-profile-menu cursor-pointer d-flex align-items-center gap-2 p-1 pe-2 rounded-pill hover-bg-slate border"
                                onClick={() => setProfileDropdownOpen(prev => !prev)}
                                style={{ cursor: 'pointer', transition: 'all 0.2s', background: profileDropdownOpen ? 'rgba(99, 102, 241, 0.08)' : 'transparent' }}
                                title="პროფილის მენიუ"
                            >
                                {user.profile_picture ? (
                                    <img 
                                        src={user.profile_picture.startsWith('http') ? user.profile_picture : API_URL + user.profile_picture} 
                                        alt={user.username}
                                        className="avatar-circle"
                                    />
                                ) : (
                                    <div className="avatar-circle-icon">
                                        <User size={18} />
                                    </div>
                                )}
                                <div className="d-none d-md-block text-start">
                                    <div className="text-dark fw-bold small leading-tight">{user.username}</div>
                                    <div className="text-muted smallest leading-tight">{getRoleLabel(user.role)}</div>
                                </div>
                                <ChevronDown 
                                    size={14} 
                                    className="text-muted ms-1" 
                                    style={{ 
                                        transform: profileDropdownOpen ? 'rotate(180deg)' : 'none', 
                                        transition: 'transform 0.2s' 
                                    }} 
                                />
                            </div>

                            {/* Dropdown Popover */}
                            <AnimatePresence>
                                {profileDropdownOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 8, scale: 0.96 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 8, scale: 0.96 }}
                                        transition={{ duration: 0.15 }}
                                        className="position-absolute end-0 mt-2 bg-white rounded-3 shadow-lg border overflow-hidden"
                                        style={{ width: '240px', zIndex: 1050 }}
                                    >
                                        {/* User Details Header */}
                                        <div className="p-3 bg-light border-bottom">
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <span className="fw-bold text-dark small">{user.username}</span>
                                                <span className="badge bg-success bg-opacity-10 text-success rounded-pill smallest px-2 py-0">Online</span>
                                            </div>
                                            <div className="text-muted smallest text-truncate">{user.email || getRoleLabel(user.role)}</div>
                                        </div>

                                        {/* Dropdown Items */}
                                        <div className="p-1">
                                            <button 
                                                className="w-100 btn btn-sm text-start d-flex align-items-center gap-2 px-3 py-2 text-dark rounded hover-bg-slate border-0"
                                                onClick={() => {
                                                    setProfileDropdownOpen(false);
                                                    navigate('/profile');
                                                }}
                                            >
                                                <User size={16} className="text-primary" />
                                                <span>ჩემი პროფილი</span>
                                            </button>

                                            <button 
                                                className="w-100 btn btn-sm text-start d-flex align-items-center gap-2 px-3 py-2 text-dark rounded hover-bg-slate border-0"
                                                onClick={() => {
                                                    setProfileDropdownOpen(false);
                                                    navigate('/admin');
                                                }}
                                            >
                                                <LayoutDashboard size={16} className="text-indigo" />
                                                <span>ადმინ პანელი</span>
                                            </button>

                                            <a 
                                                href="/ka" 
                                                target="_blank" 
                                                rel="noreferrer"
                                                className="w-100 btn btn-sm text-start d-flex align-items-center justify-content-between px-3 py-2 text-dark rounded hover-bg-slate border-0 text-decoration-none"
                                                onClick={() => setProfileDropdownOpen(false)}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <Globe size={16} className="text-muted" />
                                                    <span>საიტის ნახვა</span>
                                                </div>
                                                <ExternalLink size={12} className="text-muted" />
                                            </a>
                                        </div>

                                        {/* Logout Button */}
                                        <div className="p-1 border-top">
                                            <button 
                                                className="w-100 btn btn-sm text-start d-flex align-items-center gap-2 px-3 py-2 text-danger rounded hover-bg-slate border-0 fw-semibold"
                                                onClick={() => {
                                                    setProfileDropdownOpen(false);
                                                    logout();
                                                    navigate('/login');
                                                }}
                                            >
                                                <LogOut size={16} />
                                                <span>სისტემიდან გასვლა</span>
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </header>

                {/* ── PAGE BODY WITH FRAMER MOTION ANIMATION ── */}
                <main className="admin-page-container">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={location.pathname}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.2 }}
                        >
                            {children}
                        </motion.div>
                    </AnimatePresence>
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
